import React, { useEffect, useRef, useState } from "react";
import "./AssistantMascot.css";

const API_URL =
  process.env.REACT_APP_ASSISTANT_API_URL ||
  "https://cms.amservices.eu/api/assistant.php";

const STARTERS = [
  "Qui fait quoi demain ?",
  "Quels appels ai-je à faire ?",
  "Que peux-tu faire ?",
  "J’ai une question générale",
  "Aide-moi à formuler un message client",
];

const EXPRESSIONS = {
  happy: "/assets/ami-mascot.png",
  attentive: "/assets/ami-attentive.png",
  blink: "/assets/ami-blink.png",
  thinking: "/assets/ami-thinking.png",
  talking: "/assets/ami-talking.png",
};

function displayName(user) {
  return user?.nom || user?.name || user?.login || user?.username || "collègue";
}

function renderInlineMarkdown(text, keyPrefix) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => (
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${keyPrefix}-strong-${index}`}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>
  ));
}

function AssistantMessageContent({ content }) {
  const lines = String(content || "").replace(/\\n/g, "\n").split("\n");
  return <div className="assistant-message-content">
    {lines.map((line, index) => {
      const bullet = line.match(/^\s*[-•]\s+(.+)$/);
      const numbered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
      if (bullet) return <div className="assistant-markdown-item" key={`line-${index}`}><span>•</span><div>{renderInlineMarkdown(bullet[1], `line-${index}`)}</div></div>;
      if (numbered) return <div className="assistant-markdown-item" key={`line-${index}`}><span>{numbered[1]}.</span><div>{renderInlineMarkdown(numbered[2], `line-${index}`)}</div></div>;
      if (line.trim() === "") return <div className="assistant-markdown-space" aria-hidden="true" key={`line-${index}`} />;
      return <div key={`line-${index}`}>{renderInlineMarkdown(line, `line-${index}`)}</div>;
    })}
  </div>;
}

export default function AssistantMascot({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [panelSize, setPanelSize] = useState(null);
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("Je réfléchis…");
  const [expression, setExpression] = useState("happy");
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content: `Bonjour ${displayName(user)} ! Je suis Ami. Tu peux me poser une question sur le CRM ou sur n’importe quel autre sujet. Comment puis-je t’aider ?`,
    },
  ]);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const panelRef = useRef(null);
  const messagesRef = useRef(null);
  const reactionTimerRef = useRef(null);
  const expressionTimerRef = useRef(null);
  const requestControllerRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const pendingActionRef = useRef(null);
  const conversationMemoryRef = useRef({});

  useEffect(() => {
    Object.values(EXPRESSIONS).forEach((source) => {
      const image = new Image();
      image.src = source;
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [isOpen]);

  useEffect(() => {
    const container = messagesRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages, isLoading]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  useEffect(() => {
    if (isLoading) {
      setExpression("thinking");
      const searchExpressions = ["thinking", "attentive", "thinking", "blink"];
      const progressLabels = ["Je cherche la bonne source…", "Je vérifie les informations…", "Je prépare une réponse claire…"];
      let searchStep = 0;
      let progressStep = 0;
      const searchTimer = window.setInterval(() => {
        searchStep = (searchStep + 1) % searchExpressions.length;
        setExpression(searchExpressions[searchStep]);
      }, 720);
      const progressTimer = window.setInterval(() => {
        setLoadingLabel(progressLabels[progressStep % progressLabels.length]);
        progressStep += 1;
      }, 2800);
      expressionTimerRef.current = searchTimer;
      return () => {
        window.clearInterval(searchTimer);
        window.clearInterval(progressTimer);
      };
    }

    let blinkTimer;
    let blinkEndTimer;

    const scheduleBlink = () => {
      const delay = 3200 + Math.random() * 3600;
      blinkTimer = window.setTimeout(() => {
        setExpression((current) => {
          if (["thinking", "talking"].includes(current)) return current;
          return "blink";
        });

        blinkEndTimer = window.setTimeout(() => {
          setExpression((current) => (
            current === "blink" ? (isOpen ? "attentive" : "happy") : current
          ));
          scheduleBlink();
        }, 190);
      }, delay);
    };

    scheduleBlink();
    return () => {
      window.clearTimeout(blinkTimer);
      window.clearTimeout(blinkEndTimer);
    };
  }, [isLoading, isOpen]);

  useEffect(() => () => {
    window.clearTimeout(reactionTimerRef.current);
    window.clearInterval(expressionTimerRef.current);
    requestControllerRef.current?.abort();
  }, []);

  const stopResponse = () => {
    stopRequestedRef.current = true;
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    window.clearInterval(expressionTimerRef.current);
    setIsLoading(false);
    setExpression("attentive");
  };

  const sendMessage = async (text) => {
    const currentAttachment = attachment;
    const question = text.trim() || (currentAttachment ? "Analyse cette photo et rapproche son contenu du stock." : "");
    if (!question || isLoading) return;

    const previousMessages = messages.slice(-10);
    window.clearInterval(expressionTimerRef.current);
    window.clearTimeout(reactionTimerRef.current);
    setMessages((current) => [...current, {
      role: "user",
      content: question,
      image: currentAttachment?.dataUrl || null,
    }]);
    setDraft("");
    setAttachment(null);
    setLoadingLabel("Je comprends ta demande…");
    setIsLoading(true);
    stopRequestedRef.current = false;
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const requestTimeout = window.setTimeout(() => controller.abort(), 75000);

    try {
      const token =
        localStorage.getItem("am_token") ||
        user?.token ||
        localStorage.getItem("ams_token") ||
        "";

      const response = await fetch(API_URL, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Auth-Token": token,
        },
        body: JSON.stringify({
          message: question,
          image: currentAttachment?.dataUrl || null,
          history: previousMessages,
          pending_action: pendingActionRef.current,
          memory: conversationMemoryRef.current,
          context: {
            path: window.location.pathname,
            userName: displayName(user),
          },
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (_error) {
        throw new Error("Le serveur de l’assistant a renvoyé une réponse invalide.");
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || "L’assistant est momentanément indisponible.");
      }

      pendingActionRef.current = data.pending_action || null;
      conversationMemoryRef.current = data.memory && typeof data.memory === "object"
        ? data.memory
        : conversationMemoryRef.current;

      setMessages((current) => [
        ...current,
        { role: "assistant", content: data.answer },
      ]);
      setExpression("talking");
      window.clearTimeout(reactionTimerRef.current);
      let talking = true;
      expressionTimerRef.current = window.setInterval(() => {
        talking = !talking;
        setExpression(talking ? "talking" : "attentive");
      }, 360);
      reactionTimerRef.current = window.setTimeout(() => {
        window.clearInterval(expressionTimerRef.current);
        setExpression("attentive");
      }, 2400);
    } catch (error) {
      if (error.name === "AbortError" && stopRequestedRef.current) return;
      setMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: error.name === "AbortError"
            ? "La réponse a pris trop de temps. Tu peux reformuler la question et réessayer."
            : `Je n’ai pas pu répondre : ${error.message}`,
          isError: true,
        },
      ]);
      setExpression("attentive");
    } finally {
      window.clearTimeout(requestTimeout);
      if (requestControllerRef.current === controller) {
        requestControllerRef.current = null;
      }
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(draft);
  };

  const chooseAttachment = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMessages((current) => [...current, { role: "assistant", content: "Choisis une photo ou une image.", isError: true }]);
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setMessages((current) => [...current, { role: "assistant", content: "La photo est trop volumineuse (12 Mo maximum).", isError: true }]);
      return;
    }
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(1, 1800 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "white";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      setAttachment({ name: file.name || "Photo", dataUrl: canvas.toDataURL("image/jpeg", 0.82) });
      window.setTimeout(() => inputRef.current?.focus(), 40);
    } finally {
      bitmap.close();
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const toggleExpanded = () => {
    setIsExpanded((current) => !current);
    window.setTimeout(() => inputRef.current?.focus(), 80);
  };

  const startResize = (event) => {
    if (isExpanded || window.matchMedia("(max-width: 600px)").matches) return;
    event.preventDefault();
    const panel = panelRef.current;
    if (!panel) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = panel.offsetWidth;
    const startHeight = panel.offsetHeight;
    document.body.classList.add("assistant-is-resizing");

    const resize = (moveEvent) => {
      const maxWidth = Math.max(340, window.innerWidth - 48);
      const maxHeight = Math.max(420, window.innerHeight - 48);
      setPanelSize({
        width: Math.min(maxWidth, Math.max(340, startWidth - (moveEvent.clientX - startX))),
        height: Math.min(maxHeight, Math.max(420, startHeight - (moveEvent.clientY - startY))),
      });
    };
    const stopResize = () => {
      document.body.classList.remove("assistant-is-resizing");
      window.removeEventListener("pointermove", resize);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
    };
    window.addEventListener("pointermove", resize);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);
  };

  return (
    <div className={`assistant-mascot ${isOpen ? "is-open" : ""} ${isLoading ? "is-thinking" : ""} expression-${expression}`}>
      {isOpen && (
        <section
          ref={panelRef}
          className={`assistant-panel ${isExpanded ? "is-expanded" : ""}`}
          style={!isExpanded && panelSize ? {
            width: `${panelSize.width}px`,
            height: `${panelSize.height}px`,
          } : undefined}
          aria-label="Assistant du CRM"
        >
          <header className="assistant-header">
            <div className="assistant-identity">
              <div className="assistant-avatar-small">
                <img src={EXPRESSIONS[expression]} alt="" />
              </div>
              <div>
                <strong>Ami</strong>
                <span><i aria-hidden="true" /> Assistant CRM</span>
              </div>
            </div>
            <div className="assistant-header-actions">
              <img
                className="assistant-brand-logo"
                src="/assets/amservices-logo.png"
                alt="AM Services"
              />
              <button
                type="button"
                className="assistant-window-button"
                onClick={toggleExpanded}
                aria-label={isExpanded ? "Réduire la fenêtre" : "Agrandir la fenêtre"}
                title={isExpanded ? "Réduire" : "Agrandir"}
              >
                {isExpanded ? "↙" : "↗"}
              </button>
              <button
                type="button"
                className="assistant-close"
                onClick={() => setIsOpen(false)}
                aria-label="Fermer l’assistant"
              >
                ×
              </button>
            </div>
          </header>

          <button
            type="button"
            className="assistant-resize-handle"
            onPointerDown={startResize}
            onDoubleClick={toggleExpanded}
            aria-label="Redimensionner la fenêtre d’Ami"
            title="Glisser pour redimensionner · double-cliquer pour agrandir"
          />

          <div className="assistant-messages" ref={messagesRef} aria-live="polite">
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`assistant-message ${message.role} ${message.isError ? "error" : ""}`}
              >
                {message.image && <img className="assistant-message-image" src={message.image} alt="Pièce jointe envoyée" />}
                <AssistantMessageContent content={message.content} />
              </div>
            ))}

            {messages.length === 1 && (
              <div className="assistant-starters">
                {STARTERS.map((starter) => (
                  <button key={starter} type="button" onClick={() => sendMessage(starter)}>
                    {starter}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="assistant-message assistant typing" aria-label="Ami réfléchit">
                <span /><span /><span /><em>{loadingLabel}</em>
              </div>
            )}
          </div>

          <div className="assistant-composer">
          {attachment && (
            <div className="assistant-attachment-preview">
              <img src={attachment.dataUrl} alt="Pièce jointe à analyser" />
              <span>{attachment.name}</span>
              <button type="button" onClick={() => setAttachment(null)} aria-label="Retirer la photo">×</button>
            </div>
          )}
          <form className="assistant-form" onSubmit={handleSubmit}>
            <input
              ref={fileInputRef}
              className="assistant-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => chooseAttachment(event.target.files?.[0])}
            />
            <button
              type="button"
              className="assistant-attach"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              aria-label="Prendre ou joindre une photo"
              title="Prendre ou joindre une photo"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4 7.5 6H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3.5L15 4H9Zm3 13.2A4.2 4.2 0 1 1 12 8.8a4.2 4.2 0 0 1 0 8.4Z" /></svg>
            </button>
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                if (!isLoading) setExpression(event.target.value.trim() ? "attentive" : "happy");
              }}
              onFocus={() => {
                if (!isLoading) setExpression("attentive");
              }}
              onBlur={() => {
                if (!isLoading && !draft.trim()) setExpression("happy");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit(event);
                }
              }}
              placeholder="Pose ta question à Ami…"
              maxLength={2000}
              rows={1}
              disabled={isLoading}
              aria-label="Votre question"
            />
            {isLoading ? (
              <button
                type="button"
                className="assistant-stop"
                onClick={stopResponse}
                aria-label="Arrêter la réponse"
                title="Arrêter"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M7 7h10v10H7z" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
              disabled={!draft.trim() && !attachment}
                aria-label="Envoyer la question"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3.4 20.4 21 12 3.4 3.6l.1 6.5 11.1 1.9-11.1 1.9-.1 6.5Z" />
                </svg>
              </button>
            )}
          </form>
          <p className="assistant-disclaimer">Vérifie les informations importantes avant de les utiliser.</p>
          </div>
        </section>
      )}

      {!isOpen && <span className="assistant-hint">Une question ?</span>}
      <button
        type="button"
        className="assistant-launcher"
        onClick={() => setIsOpen((current) => {
          const next = !current;
          if (!isLoading) setExpression(next ? "attentive" : "happy");
          return next;
        })}
        onMouseEnter={() => {
          if (!isLoading && expression !== "talking") setExpression("attentive");
        }}
        onMouseLeave={() => {
          if (!isLoading && !isOpen && expression !== "talking") setExpression("happy");
        }}
        aria-label={isOpen ? "Fermer l’assistant Ami" : "Ouvrir l’assistant Ami"}
        aria-expanded={isOpen}
      >
        <span className="assistant-launcher-glow" />
        <span className="assistant-orbit" aria-hidden="true"><i /><i /><i /></span>
        <img src={EXPRESSIONS[expression]} alt={`Mascotte Ami, expression ${expression}`} />
      </button>
    </div>
  );
}
